<template>
  <form @submit.prevent="save">
    <b-message type="is-danger" v-if="errors.length > 0">
      <p v-for="error in errors">{{error}}</p>
    </b-message>
    <div class="columns is-multiline">
      <div class="column is-6">
        <b-field>
          <form-input :label="`${$t('projects.title')} *`" v-model="form.name"></form-input>
        </b-field>
      </div>
      <div class="column is-12">
        <b-field>
          <form-textarea :label="`${$t('interface.description')} *`" v-model="form.description"></form-textarea>
        </b-field>
      </div>
      <div class="column is-6">
        <b-field>
          <form-select :label="`${$t('projects.projectType')} *`" v-model="form.category">
            <option v-for="category in categories" :value="category.id">{{category.name}}</option>
          </form-select>
        </b-field>
      </div>
      <div class="column is-6">
        <b-field>
          <form-select :label="`${$t('projects.branch')} *`" v-model="form.branch">
            <option v-for="branch in branches" :value="branch.id">{{branch.name}}</option>
          </form-select>
        </b-field>
      </div>
      <div class="column is-6">
        <b-field>
          <form-select :label="`${$t('projects.state')} *`" v-model="form.state">
            <option value="new">{{ $t('projectControl.states.new') }}</option>
            <option value="contractor_definition">{{ $t('projectControl.states.contractor_definition') }}</option>
            <option value="in_process">{{ $t('projectControl.states.in_process') }}</option>
            <option value="completed">{{ $t('projectControl.states.completed') }}</option>
            <option value="cancelled">{{ $t('projectControl.states.cancelled') }}</option>
          </form-select>
        </b-field>
      </div>
      <div class="column is-6">
        <b-field>
          <form-select :label="`${$t('projects.sourceType')} *`" v-model="form.source_type">
            <option value="internal">{{ $t('projectControl.sourceTypes.internal') }}</option>
            <option value="external">{{ $t('projectControl.sourceTypes.external') }}</option>
          </form-select>
        </b-field>
      </div>
      <div class="column is-6">
        <b-field>
          <GovernmentAgencySelect v-model="form.customer"
                                  :name="form.customer__name"
                                  @select="onCustomerSelect"
                                  :label="`${$t('projects.customer')} *`">
          </GovernmentAgencySelect>
        </b-field>
      </div>
      <div class="column is-6">
        <b-field>
          <GovernmentAgencySelect v-model="form.beneficiary"
                                  :name="form.beneficiary__name"
                                  @select="onBeneficiarySelect"
                                  :label="`${$t('projects.beneficiary')} *`">
          </GovernmentAgencySelect>
        </b-field>
      </div>
      <div class="column is-6">
        <b-field>
          <form-select :label="`${$t('projects.department')} *`" v-model="form.department">
            <option v-for="department in departments" :value="department.id">{{department.name}}</option>
          </form-select>
        </b-field>
      </div>
      <div class="column is-6">
        <b-field>
          <form-input :label="`${$t('projects.budget')} *`" v-model="form.plan_budget" type="currency"></form-input>
        </b-field>
      </div>
      <div class="column is-6">
        <b-field>
          <form-datepicker :label="`${$t('projects.planStartDate')} *`" v-model="form.plan_start_date"></form-datepicker>
        </b-field>
      </div>
      <div class="column is-6">
        <b-field>
          <form-datepicker :label="`${$t('projects.planEndDate')} *`" v-model="form.plan_end_date"></form-datepicker>
        </b-field>
      </div>
      <div class="column is-6">
        <b-field>
          <form-datepicker :label="$t('projects.factStartDate')" v-model="form.fact_start_date"></form-datepicker>
        </b-field>
      </div>
      <div class="column is-6">
        <b-field>
          <form-datepicker :label="$t('projects.factEndDate')" v-model="form.fact_end_date"></form-datepicker>
        </b-field>
      </div>
      <div class="column is-8">
        <b-field>
          <EmployeeSelect :initials.sync="form.maintainer__initials"
                          :scope-all="true"
                          :label="$t('projects.maintainer')"
                          v-model="form.maintainer">
          </EmployeeSelect>
        </b-field>
      </div>
      <div class="column is-12">
        <div class="field is-grouped mt-10">
          <div class="control">
            <button type="submit" class="button is-primary">{{ $t('interface.save') }}</button>
          </div>
          <div class="control">
            <button class="button is-danger" @click.prevent="cancel">{{ $t('interface.cancel') }}</button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
  import {mapState, mapGetters} from 'vuex'
  import {prepareDate} from '@/utils'
  import EmployeeSelect from '@/components/EmployeeSelect'
  import GovernmentAgencySelect from '@/components/GovernmentAgencySelect'

  const defaultProjectData = {
    name: '',
    description: '',
    source_type: 'internal',
    category: '',
    branch: '',
    state: 'new',
    customer: null,
    customer__name: null,
    beneficiary: null,
    beneficiary__name: null,
    department: null,
    plan_budget: null,
    plan_start_date: new Date(),
    plan_end_date : new Date(),
    fact_start_date: null,
    fact_end_date: null,
    maintainer: null,
    maintainer__initials: null,
  }

  export default {
    props: ['projectData', 'editMode'],
    components: {
      GovernmentAgencySelect,
      EmployeeSelect
    },
    data() {
      return {
        form: this.projectData || {...defaultProjectData},
        isEdit: this.editMode || false,
        errors: []
      }
    },
    methods: {
      validate() {
        this.errors = []
        if (this.form.name === '' || this.form.description === '' || this.form.description === ''
          || this.form.category === '' || this.form.branch === '' || this.form.customer === null
          || this.form.beneficiary === null  || this.form.description === '') {
          this.errors.push(this.$t('interface.requiredFields'))
        } else if (this.form.plan_budget <= 0) {
          this.errors.push(this.$t('projects.budgetError'))
        }
        return this.errors.length === 0
      },
      save() {
        if (this.validate()) {
          this.$emit('save', {
            ...this.form,
            plan_start_date: prepareDate(this.form.plan_start_date),
            plan_end_date: prepareDate(this.form.plan_end_date),
            fact_start_date: this.form.fact_start_date ? prepareDate(this.form.fact_start_date) : null,
            fact_end_date: this.form.fact_end_date ? prepareDate(this.form.fact_end_date) : null
          })
        }
      },
      cancel() {
        this.$router.go(-1)
      },
      onCustomerSelect(selected) {
        this.form.beneficiary = selected.key
        this.form.beneficiary__name = selected.value
      },
      onBeneficiarySelect() {
        this.form.beneficiary__name = null
      }
    },
    mounted() {
      this.$store.dispatch('Project/getRelations')
    },
    computed: {
      ...mapState('Project', ['categories', 'branches', 'departments'])
    },
    watch: {
      projectData(data) {
        this.form = data
      }
    }
  }
</script>